import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { TLinearRegression, TLogitRegression } from "../types/worker";
import { TColumnName, TCorrelations, TPCA, TTau } from "../types";

export type TWorkerState = "idle" | "busy";

type TInitState = {
  cache: {
    logisticRegression: Record<string, TLogitRegression>;
    linearRegression: Record<string, TLinearRegression>;
    pca: Record<string, TPCA | undefined>;
    silhouetteScore: Record<string, number | undefined>;
    correlations: Record<string, TCorrelations | undefined>;
  };
  workers: {
    logisticRegression: {
      state: TWorkerState;
    };
    linearRegression: {
      state: TWorkerState;
    };
    pca: {
      state: TWorkerState;
    };
    silhouetteScore: {
      state: TWorkerState;
    };
    tau: {
      state: TWorkerState;
    };
    dCorX: {
      state: TWorkerState;
    };
    dCorY: {
      state: TWorkerState;
    };
    dCorZ: {
      state: TWorkerState;
    };
  };
};

const initialState: TInitState = {
  cache: {
    logisticRegression: {},
    linearRegression: {},
    pca: {},
    silhouetteScore: {},
    correlations: {},
  },
  workers: {
    logisticRegression: {
      state: "idle",
    },
    linearRegression: {
      state: "idle",
    },
    pca: {
      state: "idle",
    },
    silhouetteScore: {
      state: "idle",
    },
    tau: {
      state: "idle",
    },
    dCorX: {
      state: "idle",
    },
    dCorY: {
      state: "idle",
    },
    dCorZ: {
      state: "idle",
    },
  },
};

const setWorkerState = (
  workers: TInitState["workers"],
  workerName: keyof TInitState["workers"],
  state: TWorkerState
) => ({
  ...workers,
  [workerName]: {
    ...workers.logisticRegression,
    state,
  },
});

const workerSlice = createSlice({
  name: "worker",
  initialState,
  reducers: {
    generateScores: (state) => state,

    logisticRegression: (
      state,
      _action: PayloadAction<{
        trainingData: number[][];
        key: string;
        labels: number[];
      }>
    ) => ({
      ...state,
      workers: setWorkerState(state.workers, "logisticRegression", "busy"),
    }),

    setLogisticRegression: (
      state,
      action: PayloadAction<{ key: string; balancedAccuracy: number }>
    ) => ({
      ...state,
      workers: setWorkerState(state.workers, "logisticRegression", "idle"),
      cache: {
        ...state.cache,
        logisticRegression: {
          ...state.cache.logisticRegression,
          [action.payload.key]: {
            balancedAccuracy: action.payload.balancedAccuracy,
          },
        },
      },
    }),

    linearRegression: (
      state,
      _action: PayloadAction<{
        trainingData: number[][];
        key: string;
      }>
    ) => ({
      ...state,
      workers: setWorkerState(state.workers, "linearRegression", "busy"),
    }),

    setLinearRegression: (
      state,
      action: PayloadAction<{ key: string; coefOfDetermination: number }>
    ) => ({
      ...state,
      workers: setWorkerState(state.workers, "linearRegression", "idle"),
      cache: {
        ...state.cache,
        linearRegression: {
          ...state.cache.linearRegression,
          [action.payload.key]: {
            coefOfDetermination: action.payload.coefOfDetermination,
          },
        },
      },
    }),

    silhouetteScore: (
      state,
      _action: PayloadAction<{
        key: string;
        data: number[][];
        labels: number[];
      }>
    ) => ({
      ...state,
      workers: setWorkerState(state.workers, "silhouetteScore", "busy"),
    }),

    setSilhouetteScore: (
      state,
      action: PayloadAction<{
        key: string;
        silhouetteScore: number;
      }>
    ) => ({
      ...state,
      workers: setWorkerState(state.workers, "silhouetteScore", "idle"),
      cache: {
        ...state.cache,
        silhouetteScore: {
          ...state.cache.silhouetteScore,
          [action.payload.key]: action.payload.silhouetteScore,
        },
      },
    }),

    /**
     * PCA
     */
    calculatePCA: (
      state,
      _action: PayloadAction<{
        selectedDataset: string;
        selectedMethod: TColumnName;
      }>
    ) => state,

    setPCA: (
      state,
      action: PayloadAction<{
        key: string;
        pca: TPCA;
      }>
    ) => ({
      ...state,
      cache: {
        ...state.cache,
        pca: {
          ...state.cache.pca,
          [action.payload.key]: action.payload.pca,
        },
      },
    }),

    setProjectToPCA: (
      state,
      action: PayloadAction<{
        key: string;
        adjustedData: number[][];
      }>
    ) => ({
      ...state,
      cache: {
        ...state.cache,
        pca: {
          ...state.cache.pca,
          [action.payload.key]: {
            // TODO remove !
            ...state.cache.pca[action.payload.key]!,
            adjustedData: action.payload.adjustedData,
          },
        },
      },
    }),

    /**
     * end of PCA
     */

    setCorrelations: (
      state,
      action: PayloadAction<{
        key: string;
        correlations: TCorrelations;
      }>
    ) => ({
      ...state,
      cache: {
        ...state.cache,
        correlations: {
          ...state.cache.correlations,
          [action.payload.key]: action.payload.correlations,
        },
      },
    }),

    calculateTau: (
      state,
      _action: PayloadAction<{
        key: string;
        X: number[];
        Y: number[];
        Z: number[];
        V: number[];
      }>
    ) => ({
      ...state,
      workers: setWorkerState(state.workers, "tau", "busy"),
    }),

    setTau: (
      state,
      action: PayloadAction<{
        key: string;
        tau: TTau;
      }>
    ) => ({
      ...state,
      workers: setWorkerState(state.workers, "tau", "idle"),
      cache: {
        ...state.cache,
        correlations: {
          ...state.cache.correlations,
          [action.payload.key]: {
            ...(state.cache.correlations[action.payload.key] || {}),
            ...action.payload.tau,
          },
        },
      },
    }),

    setDCor: (
      state,
      action: PayloadAction<{
        coordinate: "x" | "y" | "z";
        key: string;
        dCor: number;
      }>
    ) => ({
      ...state,
      workers: setWorkerState(
        state.workers,
        `dCor${action.payload.coordinate.toUpperCase()}` as keyof TInitState["workers"],
        "idle"
      ),
      cache: {
        ...state.cache,
        correlations: {
          ...state.cache.correlations,
          [action.payload.key]: {
            ...(state.cache.correlations[action.payload.key] || {}),
            [`dCor_${action.payload.coordinate}`]: action.payload.dCor,
          },
        },
      },
    }),

    calculateDCorX: (
      state,
      _action: PayloadAction<{ key: string; X: number[]; Y: number[] }>
    ) => ({
      ...state,
      workers: setWorkerState(state.workers, "dCorX", "busy"),
    }),
    calculateDCorY: (
      state,
      _action: PayloadAction<{ key: string; X: number[]; Y: number[] }>
    ) => ({
      ...state,
      workers: setWorkerState(state.workers, "dCorY", "busy"),
    }),
    calculateDCorZ: (
      state,
      _action: PayloadAction<{ key: string; X: number[]; Y: number[] }>
    ) => ({
      ...state,
      workers: setWorkerState(state.workers, "dCorZ", "busy"),
    }),
  },
});

export const workerActions = workerSlice.actions;
export default workerSlice;
